import type { UpdateOrderStatusParams } from '@/entities/order/model'
import { Badge, Button, Popover, Radio, Stack, Text } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import { useMemo, useState } from 'react'
import { getOrderType } from '@/entities/order/lib'
import { useOrderStatusMutation, useRawOrdersQuery } from '@/entities/order/model'
import { deriveStatusOptions, useCollectionCodeEnabled, useStateConfigQuery, useTmInitialsEnabled } from '@/entities/state/model'
import { CRATE_SCAN_PENDING_MESSAGE, notifyGroupScanIncomplete, notifyOrderScanIncomplete, useCrateScanGate, useGroupTransitionGuard, useOrderGroupMembers, useOrderTransitionGuard, usePoaGate } from '@/features/order/lib'
import { useCollectionCodeModal } from '@/features/order/ui/collection-code-modal'
import { useTmInitialsStore } from '@/features/order/ui/initials-capture-modal'
import { useNoBarcodeCompletionConfirm } from '@/features/order/ui/no-barcode-confirmation'
import { RadioIconCheck } from '@/shared/ui/radio-icon-check'
import classes from './StatusUpdatePopover.module.css'

interface StatusUpdatePopoverProps {
  orderId: string
  currentStatus: string
  lastUpdateTime?: string
  onStatusUpdate?: (status: string) => void
  /**
   * Optional custom trigger render function
   */
  renderTrigger?: (props: { onClick: () => void, isOpen: boolean }) => React.ReactNode
}

export function StatusUpdatePopover({
  orderId,
  currentStatus,
  lastUpdateTime,
  onStatusUpdate,
  renderTrigger,
}: StatusUpdatePopoverProps) {
  const [opened, setOpened] = useState(false)
  const [selectedStatus, setSelectedStatus] = useState<string>(currentStatus)
  const [isUpdating, setIsUpdating] = useState(false)
  const { data: allOrders } = useRawOrdersQuery()
  const isCollectionCodeEnabled = useCollectionCodeEnabled()

  function handleOpenChange(isOpen: boolean) {
    setOpened(isOpen)
    if (!isOpen) {
      // Reset to current status when closing without updating
      setSelectedStatus(currentStatus)
    }
  }

  const { data, isLoading: isLoadingStates } = useStateConfigQuery()
  const isTmInitialsEnabled = useTmInitialsEnabled()
  const { mutateAsync: updateStatusMutation, isPending } = useOrderStatusMutation()
  const groupMemberOrderIds = useOrderGroupMembers(orderId)
  // BD-8186: the popover drives the mutation itself, so it must consult the same gate.
  const { isTransitionBlocked } = useGroupTransitionGuard(orderId)
  const { open: openCollectionCodeModal } = useCollectionCodeModal()
  const enqueueInitials = useTmInitialsStore(state => state.enqueue)
  const { shouldGateCompletion, openPoaChecks } = usePoaGate()
  const { isCrateScanPending } = useCrateScanGate()
  const { requiresConfirm, requestConfirm } = useNoBarcodeCompletionConfirm()
  const order = allOrders?.find(o => o.orderId === orderId)
  // BD-8130: the group moves together, so the BD-8589 confirmation covers every member.
  const loadedGroupMembers = allOrders?.filter(o => groupMemberOrderIds.includes(o.orderId)) ?? []
  const groupMemberOrders = loadedGroupMembers.length > 0 ? loadedGroupMembers : (order ? [order] : [])
  // BD-8644: a single order gets the same rule on its own path.
  const { isOrderTransitionBlocked } = useOrderTransitionGuard(order)
  const normalizedOrderType = getOrderType(order?.hsFields)?.trim().toLowerCase()
  const configuredEligibleOrderTypes = (data?.collectCodeEligibleOrderTypes ?? [])
    .map(type => type.trim().toLowerCase())
    .filter(Boolean)
  const isCollectionCodeEligibleOrder = configuredEligibleOrderTypes.includes(normalizedOrderType ?? '')

  const statusOptions = useMemo(() => {
    return deriveStatusOptions(data?.states)
  }, [data])

  /**
   * Handles the status update
   */
  async function handleStatusUpdate(newStatus: string) {
    if (!newStatus || !orderId || newStatus === currentStatus)
      return

    // BD-8186: the group stays in Arrived until every order in it is fully scanned.
    if (isTransitionBlocked(newStatus)) {
      notifyGroupScanIncomplete()
      return
    }
    if (isOrderTransitionBlocked(newStatus)) {
      notifyOrderScanIncomplete()
      return
    }

    // BD-8589: completing without array_barcodes waits for the team member to confirm.
    // The popover closes first so it does not sit above the confirmation modal.
    if (order && requiresConfirm(groupMemberOrders, newStatus)) {
      handleOpenChange(false)
      requestConfirm(() => void applyStatusUpdate(newStatus))
      return
    }

    await applyStatusUpdate(newStatus)
  }

  async function applyStatusUpdate(newStatus: string) {
    // BD-8245: the manual status route is gated too — a restricted order cannot
    // be forced to Completed from the popover without running the checks.
    if (newStatus === 'Completed' && shouldGateCompletion(order)) {
      // The same crate scan gate as the quick action: an order with crates still
      // unaccounted for must not reach the checks from the manual route either.
      if (isCrateScanPending(order)) {
        notifications.show({ message: CRATE_SCAN_PENDING_MESSAGE, color: 'red' })
        setOpened(false)
        return
      }
      openPoaChecks(orderId)
      setOpened(false)
      return
    }

    if (
      newStatus === 'Completed'
      && isCollectionCodeEnabled
      && isCollectionCodeEligibleOrder
    ) {
      openCollectionCodeModal({ orderId, orderIds: groupMemberOrderIds, nextStatus: newStatus, lastUpdateTime })
      setOpened(false)
      return
    }

    setIsUpdating(true)

    try {
      // BD-8130: every status, Completed included, moves the whole group together.
      const updatePromises = groupMemberOrderIds.map((memberId) => {
        const updateParams: UpdateOrderStatusParams = {
          orderId: memberId,
          newStatus,
          lastUpdateTime,
        }
        return updateStatusMutation(updateParams)
      })

      await Promise.all(updatePromises)

      // Initials are asked once for every order this completion applied to
      // (BD-8114 Scenarios 1-2).
      if (newStatus === 'Completed' && isTmInitialsEnabled) {
        enqueueInitials({ orderId, orderIds: groupMemberOrderIds, customerName: order?.customerName })
      }

      if (onStatusUpdate) {
        onStatusUpdate(newStatus)
      }

      setOpened(false)
    } catch (error) {
      console.error('Failed to update status:', error)
    } finally {
      setIsUpdating(false)
    }
  }

  const currentStatusDisplay = statusOptions.find(option => option.value === currentStatus)?.label || currentStatus

  return (
    <Popover
      opened={opened}
      onChange={handleOpenChange}
      position="bottom-start"
      width={250}
      radius="md"
      shadow="md"
      withArrow
    >
      <Popover.Target>
        {renderTrigger ? (
          renderTrigger({
            onClick: () => setOpened(o => !o),
            isOpen: opened,
          })
        ) : (
          <Badge
            color="gray.8"
            radius="sm"
            size="lg"
            style={{ cursor: 'pointer' }}
            onClick={() => setOpened(o => !o)}
          >
            {currentStatusDisplay}
          </Badge>
        )}
      </Popover.Target>
      <Popover.Dropdown>
        <Stack>
          {isLoadingStates ? (
            <Text>Loading available statuses...</Text>
          ) : statusOptions.length === 0 ? (
            <Text>No status transitions available</Text>
          ) : (
            <Radio.Group
              value={selectedStatus}
              onChange={setSelectedStatus}
              name="orderStatus"
            >
              {statusOptions.map(option => (
                <Radio
                  classNames={{
                    radio: classes.radio,
                    body: classes.body,
                    icon: classes.icon,
                    inner: classes.inner,
                    label: classes.label,
                    labelWrapper: classes.labelWrapper,
                  }}
                  icon={RadioIconCheck}
                  key={option.value}
                  value={option.value}
                  label={option.label}
                  labelPosition="left"
                  size="lg"
                  py="8px"
                  disabled={isUpdating || isPending}
                />
              ))}
            </Radio.Group>
          )}

          <Button
            variant="filled"
            color="gray.8"
            fullWidth
            size="sm"
            radius="md"
            onClick={() => handleStatusUpdate(selectedStatus)}
            loading={isUpdating || isPending}
            disabled={selectedStatus === currentStatus
              || isUpdating
              || isPending
              || !selectedStatus
              || statusOptions.length === 0}
          >
            Update
          </Button>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  )
}
